import {Component, Input, OnInit} from "@angular/core";
import {AdditionalQuestionGroup, Order, Product, StandardName} from "../../../../store/state/order.state";
import {ProductAdditionalQuestionsFormService} from "./product-additional-questions-form.service";
import {FormGroup} from "@angular/forms";
import {BinaryQuestionData, TextQuestionData} from "../../../../store/actions/order.actions";
import {Observable, of} from "rxjs";
import {map} from "rxjs/operators";
import {groupBy} from "../../../../umbrella-common/common-functions";
import {TranslateService} from "@ngx-translate/core";
import {TranslationsHelperService} from "../../../translations/translations-helper.service";
import {BaseSubFormComponent} from "../../../../umbrella-common/components/base-sub-form.component";

@Component({
    selector: 'product-additional-questions',
    templateUrl: './product-additional-questions.template.html',
    styleUrls: ['./product-additional-questions.style.scss'],
    standalone: false
})
export class ProductAdditionalQuestionsComponent extends BaseSubFormComponent implements OnInit {

	@Input() order: Order;
	@Input() product: Product;
	@Input() textQuestions: TextQuestionData[];
	@Input() textQuestionsForGroups: TextQuestionData[];
	@Input() binaryQuestions: BinaryQuestionData[];
	@Input() blocked: boolean = false;
	@Input() translateClientsData: boolean;
	@Input() useOriginalTranslations: boolean;

	groupedTextQuestions: Observable<Map<string, TextQuestionData[]>>;

	form: FormGroup;

	constructor(public formService: ProductAdditionalQuestionsFormService, private translateService: TranslateService, public translationsHelper: TranslationsHelperService) {
		super(formService);
	}

	ngOnInit(): void {
		this.form = this.formService.buildSubForm(this.product.standard.name, this.totalSubForms(this.order), this.product, this.textQuestions, this.textQuestionsForGroups, this.binaryQuestions);

		this.groupedTextQuestions = of(this.textQuestions.map((question, index) => ({
			...question,
			index
		}))).pipe(
			map(data => groupBy(data, item => item.group))
		);
	}

	textQuestionControlName(questionIndex: number): string {
		return `text-question-${questionIndex}${this.product.standard.name}`;
	}

	binaryQuestionRadioControlName(questionIndex: number): string {
		return `binary-question-radio-${questionIndex}${this.product.standard.name}`;
	}

	binaryQuestionDescriptionControlName(questionIndex: number): string {
		return `binary-question-description-${questionIndex}${this.product.standard.name}`;
	}

	// needed for angular to not rebuild component added by *ngFor
	trackByFcn(index: number) {
		return index;
	}

	getPlaceholderText(binaryQuestion: BinaryQuestionData): string {
		if (binaryQuestion.type == 'USING_CONSULTANT') {
			return "additional-questions.placeholder.USING_CONSULTANT";
		} else if (binaryQuestion.type == 'CONFLICT_OF_INTERESTS') {
			return 'additional-questions.placeholder.CONFLICT_OF_INTERESTS';
		} else {
			return "common.text-area-placeholder";
		}
	}

	shouldHideForIso27701(): boolean {
		return this.product.standard.name === StandardName.ISO_27701 &&
			this.formService.getAllFormKeys().filter(standard => [StandardName.ISO_27001 as string, StandardName.ISO_27701 as string].includes(standard)).length == 2;
	}

	getGroupName(group: AdditionalQuestionGroup): Observable<string> {
		return this.translateService.get(`additional-questions.group.${group}`);
	}

	getTranslationSubjectForBinaryQuestion(question: BinaryQuestionData): string {
		return `additionalQuestions.binaryQuestion[${question.type}]`;
	}

	getTranslationSubjectForTextQuestion(question: TextQuestionData): string {
		return `additionalQuestions.textQuestion[${question.type}]`;
	}

	binaryQuestionTranslationVisible(question: BinaryQuestionData): boolean {
		return this.translationsHelper.translationExists(this.product.standard.name, this.getTranslationSubjectForBinaryQuestion(question), this.useOriginalTranslations);
	}

	binaryQuestionTranslatedText(question: BinaryQuestionData): string {
		return this.translationsHelper.getTranslation(this.product.standard.name, this.getTranslationSubjectForBinaryQuestion(question), this.useOriginalTranslations);
	}

	textQuestionTranslationVisible(question: TextQuestionData): boolean {
		return this.translationsHelper.translationExists(this.product.standard.name, this.getTranslationSubjectForTextQuestion(question), this.useOriginalTranslations);
	}

	textQuestionTranslatedText(question: TextQuestionData): string {
		return this.translationsHelper.getTranslation(this.product.standard.name, this.getTranslationSubjectForTextQuestion(question), this.useOriginalTranslations);
	}

	getFormKey(): string {
		return this.product.standard.name;
	}

	protected iso27701HasItsOwnSubForm(): boolean {
		return true;
	}
}
